import React, { useState, useEffect } from 'react';
import logo from '../kloudynet-logo.jpeg';

const FRAMEWORKS = 'NIST AI RMF 1.0 · ISO 42001:2023 · OWASP LLM Top 10 · MITRE ATLAS';

const scoreColor = s => s >= 4 ? '#2E7D32' : s >= 3 ? '#1565C0' : s >= 2 ? '#F57F17' : '#C62828';
const maturityLabel = s => {
  if (s >= 4.5) return 'Optimized';
  if (s >= 3.5) return 'Managed';
  if (s >= 2.5) return 'Defined';
  if (s >= 1.5) return 'Developing';
  return 'Initial';
};
const pct = (n, d) => d > 0 ? Math.round((n / d) * 100) : 0;

const SKU_MAP = [
  { domain:'D1', product:'Purview AI Hub + DSPM for AI',         sku:'E5 Compliance',         gap:'No AI register or data classification' },
  { domain:'D2', product:'Entra Agent ID + Agent 365',           sku:'E5 Identity',            gap:'Agentic AI governance gaps' },
  { domain:'D3', product:'Purview DLP + Sensitivity Labels',     sku:'E5 Compliance',          gap:'PII in AI pipelines, no Copilot DLP' },
  { domain:'D4', product:'Defender for AI + Entra Internet Access', sku:'E5 Security + Entra Suite', gap:'No prompt injection monitoring' },
  { domain:'D5', product:'Agent Store + Copilot Control System', sku:'E3 Foundational',        gap:'Shadow AI adoption, no sanctioned controls' },
  { domain:'D6', product:'Sentinel + AI Security Dashboard',     sku:'E5 Security',            gap:'Zero ATLAS detection rules' },
];

// ── Shared components ────────────────────────────────────────────────────────

function PersonaBar({ color, icon, label, displayName }) {
  // The "Back" button has been removed in M1 — role is no longer self-selected,
  // it's the role bound to the active TenantUserAssignment. Use the tenant
  // switcher in the sidebar to change context.
  return (
    <div className="home-persona-bar">
      <div className="home-persona-bar-label">
        <div className="home-persona-bar-icon" style={{background:color}}>{icon}</div>
        <span>{label}</span>
      </div>
      {displayName && (
        <span style={{marginLeft:'auto',fontSize:11,color:'#7A8899'}}>Welcome, {displayName}</span>
      )}
    </div>
  );
}

function Footer() {
  return (
    <div className="home-footer">
      <div className="home-footer-brand">SAARA v2.0 &nbsp;·&nbsp; Kloudynet</div>
      <div className="home-footer-tags">{FRAMEWORKS}</div>
    </div>
  );
}

function EmptyState({ emoji, title, desc }) {
  return (
    <div className="home-empty-state">
      <div style={{fontSize:36,marginBottom:12,opacity:0.3}}>{emoji}</div>
      <div style={{fontSize:13,fontWeight:500,color:'#455A64',marginBottom:6}}>{title}</div>
      <div style={{fontSize:12,color:'#7A8899'}}>{desc}</div>
    </div>
  );
}

function DomainBar({ domain, maxScore = 5 }) {
  const s = domain.score || 0;
  const w = pct(s, maxScore);
  return (
    <div style={{display:'flex',alignItems:'center',gap:10,padding:'6px 0'}}>
      <div style={{width:28,fontSize:10,fontWeight:700,color:domain.color,flexShrink:0}}>{domain.id}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
          <span style={{fontSize:11,fontWeight:500,color:'#0D1B2A',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{domain.name}</span>
          <span style={{fontSize:11,fontWeight:600,color:scoreColor(s),flexShrink:0}}>{s}</span>
        </div>
        <div style={{height:6,borderRadius:3,background:'#EEF0F3',overflow:'hidden'}}>
          <div style={{width:`${w}%`,height:'100%',borderRadius:3,background:scoreColor(s),transition:'width 0.4s ease'}}/>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, sub, color }) {
  return (
    <div style={{padding:'14px 16px',background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,textAlign:'center'}}>
      <div style={{fontSize:9,color:'#7A8899',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>{label}</div>
      <div style={{fontSize:22,fontWeight:700,color:color||'#0D1B2A',lineHeight:1}}>{value}</div>
      {sub && <div style={{fontSize:10,color:'#7A8899',marginTop:4}}>{sub}</div>}
    </div>
  );
}

// ── Engagement toolkit (shared between Assessor and Pre-sales) ───────────────

const TOOLKIT_DOCS = {
  pre_engagement: {
    title:'Pre-engagement', desc:'Share with client before the assessment begins',
    bg:'#E8F0FE', border:'#BBDEFB', color:'#1565C0', iconBg:'#E8F0FE',
    docs:[
      {name:'SAARA scope & methodology', desc:'Framework overview, 6 domains, 55 controls, scoring methodology — client-facing briefing document', file:'SAARA_Scope_Methodology_v2.pdf'},
      {name:'Engagement letter template', desc:'Statement of work, timeline, deliverables, confidentiality terms, assessor responsibilities', file:'SAARA_Engagement_Letter_Template.docx'},
      {name:'NDA / Confidentiality agreement', desc:'Mutual NDA covering assessment data, findings, and client environment access', file:'Kloudynet_SAARA_NDA_Template.pdf'},
      {name:'Pre-assessment questionnaire', desc:'Client profile, AI inventory, regulatory context — pre-populates the Initiate form', file:'SAARA_Pre_Assessment_Questionnaire.xlsx'},
    ],
  },
  technical: {
    title:'Technical & permissions', desc:'Client IT team must action before auto-scoring can run',
    bg:'#FFF3E0', border:'#FFE0B2', color:'#E65100', iconBg:'#FFF3E0',
    docs:[
      {name:'Microsoft tenant permissions guide', desc:'Graph API, Sentinel, Defender for Cloud, Purview, Entra ID — required OAuth scopes and service principal setup', file:'SAARA_MS_Permissions_Guide.pdf'},
      {name:'Service principal setup instructions', desc:'Step-by-step guide for client IT to create the SAARA service principal in Entra ID with correct API permissions', file:'SAARA_Service_Principal_Setup.pdf'},
      {name:'Network & firewall requirements', desc:'IP ranges, endpoints, and ports that must be allow-listed for SAARA auto-scoring to connect', file:'SAARA_Network_Requirements.pdf'},
      {name:'Data handling & privacy notice', desc:'How SAARA processes client data — transient processing, no data retention, SOC 2 compliance statement', file:'SAARA_Data_Handling_Notice.pdf'},
    ],
  },
  client_prep: {
    title:'Client preparation checklist', desc:'Send to client stakeholders to prepare for the assessment',
    bg:'#E8F5E9', border:'#A5D6A7', color:'#2E7D32', iconBg:'#E8F5E9',
    docs:[
      {name:'Document evidence checklist', desc:'List of 7 required documents + 8 supporting documents the client should gather before Day 1 of the assessment', file:'SAARA_Document_Checklist.pdf'},
      {name:'Stakeholder interview schedule', desc:'Template schedule for 6 domain interviews — suggested interviewees (CISO, CTO, DPO, IT Lead, HR, SOC Manager)', file:'SAARA_Interview_Schedule_Template.xlsx'},
      {name:'Key personnel contact sheet', desc:'Template for client to provide contact details for all stakeholders involved in the assessment', file:'SAARA_Contact_Sheet_Template.xlsx'},
    ],
  },
  post_engagement: {
    title:'Post-engagement', desc:'Deliverables and follow-up templates',
    bg:'#F3E5F5', border:'#CE93D8', color:'#6A1B9A', iconBg:'#F3E5F5',
    docs:[
      {name:'Remediation tracking template', desc:'Pre-built tracker with all 55 controls, gap status, owner fields, timelines — auto-populates from assessment data', file:'SAARA_Remediation_Tracker.xlsx'},
      {name:'Recertification timeline guide', desc:'Recommended recertification intervals by industry and regulatory context — 3, 6, or 12 months', file:'SAARA_Recertification_Guide.pdf'},
      {name:'Executive summary template', desc:'One-page executive brief template for client board presentations — auto-populates from report data', file:'SAARA_Executive_Summary_Template.pptx'},
    ],
  },
  framework: {
    title:'SAARA Framework & Assessment Tools', desc:'Core framework documents and working assessment files — download for offline use',
    bg:'#FFF8E1', border:'#FFE082', color:'#F57F17', iconBg:'#FFF8E1',
    docs:[
      {name:'SAARA Framework Document v1.2', desc:'Complete framework specification — 6 domains, 30 categories, control requirements, maturity model, threat mapping', file:'Kloudynet_SAARA_Framework_v1.2.docx'},
      {name:'SAARA Internal Assessor Guide v1.1', desc:'55 controls + 5 sub-controls with scoring guidance, assessor notes, evidence checklists, and interview questions per domain', file:'Kloudynet_SAARA_Internal_Assessor_Guide_v1.1.docx'},
      {name:'SAARA Evidence Tracker v1.0', desc:'Master assessment workbook — control definitions, framework alignment (NIST/ISO/OWASP/ATLAS), scoring guidance (1-5), risk statements, client evidence collection', file:'Kloudynet_SAARA_Evidence_Tracker_v1.0.xlsx'},
      {name:'SAARA Maturity Worksheet v1.2', desc:'Domain-level maturity scoring worksheet — current vs target scores, gap analysis, framework cross-references per control', file:'Kloudynet_SAARA_Maturity_Worksheet_v1.2.xlsx'},
      {name:'SAARA Client Guide v1.0', desc:'Client-facing overview of SAARA — what to expect, how to prepare, domain summaries, deliverables', file:'Kloudynet_SAARA_Client_Guide_v1.0.docx'},
    ],
  },
  sales: {
    title:'Sales & proposal', desc:'Collateral for scoping and closing SAARA engagements',
    bg:'#E3F2FD', border:'#90CAF9', color:'#0078D4', iconBg:'#E3F2FD',
    docs:[
      {name:'SAARA solution brief', desc:'One-page overview for prospects — value proposition, assessment process, sample outputs, pricing tiers', file:'SAARA_Solution_Brief.pdf'},
      {name:'Proposal template', desc:'Pre-built proposal with scope, methodology, deliverables, timeline, pricing — customise per client', file:'SAARA_Proposal_Template.pptx'},
      {name:'ROI calculator', desc:'Licensing uplift model — maps SAARA gaps to Microsoft SKU revenue with estimated annual contract value', file:'SAARA_ROI_Calculator.xlsx'},
      {name:'Case study template', desc:'Template for documenting completed SAARA engagements as reusable client references', file:'SAARA_Case_Study_Template.docx'},
    ],
  },
};

function EngagementToolkit({ sections, API }) {
  const cats = sections.map(s => TOOLKIT_DOCS[s]).filter(Boolean);
  const handleDownload = (file) => {
    const link = document.createElement('a');
    link.href = `${API}/api/toolkit/${encodeURIComponent(file)}`;
    link.download = file;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:20}}>
      {cats.map(cat => (
        <div key={cat.title} style={{background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,overflow:'hidden'}}>
          <div style={{padding:'12px 16px',background:cat.bg,borderBottom:`1px solid ${cat.border}`}}>
            <div style={{fontSize:12,fontWeight:600,color:cat.color}}>{cat.title}</div>
            <div style={{fontSize:10,color:'#7A8899',marginTop:1}}>{cat.desc}</div>
          </div>
          <div style={{padding:'12px 16px'}}>
            {cat.docs.map((d,i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:i<cat.docs.length-1?'1px solid #F0F2F5':'none'}}>
                <div style={{width:28,height:28,borderRadius:6,background:cat.iconBg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:700,color:cat.color,flexShrink:0}}>
                  {d.file.split('.').pop().toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A'}}>{d.name}</div>
                  <div style={{fontSize:9,color:'#7A8899',lineHeight:1.3,marginTop:1}}>{d.desc}</div>
                </div>
                <button onClick={() => handleDownload(d.file)} style={{
                  fontSize:9,padding:'4px 10px',background:'#F5F7F9',color:'#455A64',border:'1px solid #E0E4E8',
                  borderRadius:6,cursor:'pointer',fontWeight:500,whiteSpace:'nowrap',flexShrink:0,
                }}>Download</button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function Home({ API, engagement, overview, role, displayName, onNewAssessment, onRecertify, onContinue, onClientView }) {
  // Role is now derived from the active TenantUserAssignment (M1). The legacy
  // self-select persona screen and `setPersona` flow have been removed.
  const persona = role;
  const [activeEng, setActiveEng] = useState(null);
  const [domains, setDomains] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [eng, dm] = await Promise.all([
          fetch(`${API}/api/engagements/active`).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/domains`).then(r => r.json()),
        ]);
        if (eng && eng.id) {
          setActiveEng(eng);
          const r = await fetch(`${API}/api/runs/${eng.id}`).then(r => r.json());
          setRuns(r);
        }
        setDomains(dm || []);
      } catch(e) { console.error('Home data fetch failed:', e); }
      setLoading(false);
    })();
  }, [API]);

  const hasActive = !!activeEng;
  const latestRun = runs.length > 0 ? runs[runs.length - 1] : null;
  const runCount = runs.length;
  const wf = overview?.workflow;
  const scored = overview?.scored_controls || 0;
  const total = overview?.total_controls || 55;
  const gaps = overview?.gaps || 0;
  const os = overview?.overall_score || 0;

  // Find worst domains for priority findings
  const sortedDomains = [...domains].sort((a, b) => (a.score || 0) - (b.score || 0));
  const worstDomains = sortedDomains.slice(0, 3);

  // SVG icons
  const iconAssessor = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6"/><path d="M23 11h-6"/></svg>;
  const iconClient = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
  const iconSales = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>;
  const iconCSP = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="9" height="9"/><rect x="13" y="2" width="9" height="9"/><rect x="2" y="13" width="9" height="9"/><rect x="13" y="13" width="9" height="9"/></svg>;
  const iconAdmin = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>;

  // The legacy persona-selection screen has been removed in M1 — role is bound
  // to the active TenantUserAssignment, not self-selected. A user with no role
  // resolved (zero assignments, or middleware not yet populated) sees the
  // simple welcome card App.js renders before this component is even reached.
  if (!persona) {
    return (
      <div className="home-container">
        <div style={{padding:32,textAlign:'center'}}>
          <div style={{fontSize:18,fontWeight:700,color:'#0D1B2A',marginBottom:8}}>
            Welcome{displayName ? `, ${displayName}` : ''}
          </div>
          <div style={{fontSize:12,color:'#7A8899'}}>
            Pick a tenant from the sidebar to continue.
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  //  ASSESSOR — Command centre
  // ══════════════════════════════════════════════════════════════
  if (persona === 'assessor') {
    const steps = wf ? [
      { label:'Auto-scored', done:wf.auto?.confirmed||0, total:wf.auto?.total||0, unit:'confirmed', color:'#2E7D32', page:'auto' },
      { label:'Document uploads', done:wf.uploads?.scored||0, total:wf.uploads?.total||0, unit:'scored', color:'#6A1B9A', page:'uploads' },
      { label:'Interviews', done:wf.interviews?.scored||0, total:wf.interviews?.total||0, unit:'scored', color:'#455A64', page:'interviews' },
      { label:'Manual inputs', done:wf.manual?.scored||0, total:wf.manual?.total||0, unit:'scored', color:'#F57F17', page:'manual' },
    ] : [];
    const firstIncomplete = steps.find(s => s.done < s.total);

    return (
      <div className="home-container">
        <PersonaBar color="#1565C0" icon={iconAssessor} label="Kloudynet Assessor" displayName={displayName}/>

        {!loading && hasActive && engagement && (
          <>
            {/* Active engagement header */}
            <div className="home-active-banner">
              <div className="home-active-header">
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div className="home-active-dot"/>
                  <span className="home-active-label">Active — {activeEng.org_name}</span>
                  {activeEng.eng_type && activeEng.eng_type !== 'New Assessment' && (
                    <span className="pill pill-blue" style={{fontSize:9}}>{activeEng.eng_type}</span>
                  )}
                </div>
                <span className="home-active-runs">{runCount} run{runCount !== 1 ? 's' : ''}</span>
              </div>

              {/* Metric strip */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:14}}>
                <MetricCard label="Overall maturity" value={os || '—'} sub={os ? `${maturityLabel(os)} / 5.0` : 'Not yet scored'} color={os ? scoreColor(os) : '#B0BAC5'}/>
                <MetricCard label="Controls scored" value={`${scored}/${total}`} sub={`${pct(scored,total)}% complete`} color={scored >= total ? '#2E7D32' : '#F57F17'}/>
                <MetricCard label="Gaps identified" value={gaps} sub={gaps > 15 ? 'High severity' : gaps > 5 ? 'Moderate' : 'Low'} color={gaps > 15 ? '#C62828' : gaps > 5 ? '#F57F17' : '#2E7D32'}/>
                <MetricCard label="Unconfirmed auto" value={overview?.unconfirmed_count || 0} sub={overview?.unconfirmed_count > 0 ? 'Need review' : 'All clear'} color={overview?.unconfirmed_count > 0 ? '#F57F17' : '#2E7D32'}/>
              </div>

              {/* Workflow progress — the assessor's main question: "where am I?" */}
              <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',marginBottom:8}}>Assessment workflow progress</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:14}}>
                {steps.map((s,i) => {
                  const done = s.done >= s.total && s.total > 0;
                  const isNext = s === firstIncomplete;
                  return (
                    <div key={i} onClick={() => onClientView(s.page)} style={{
                      padding:'10px 12px',borderRadius:8,cursor:'pointer',
                      background: done ? '#E8F5E9' : isNext ? '#F0F6FF' : '#FAFBFC',
                      border: `1px solid ${done ? '#A5D6A7' : isNext ? '#90CAF9' : '#EEF0F3'}`,
                    }}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                        <span style={{fontSize:10,fontWeight:600,color: done ? '#2E7D32' : isNext ? '#1565C0' : '#455A64'}}>
                          {done ? '\u2713 ' : isNext ? '\u25B6 ' : ''}{s.label}
                        </span>
                        {isNext && <span style={{fontSize:8,fontWeight:700,padding:'1px 5px',borderRadius:4,background:'#1565C0',color:'#fff'}}>NEXT</span>}
                      </div>
                      <div style={{height:4,borderRadius:2,background:'#EEF0F3',overflow:'hidden',marginBottom:4}}>
                        <div style={{width:`${pct(s.done,s.total)}%`,height:'100%',borderRadius:2,background:done?'#2E7D32':s.color,transition:'width 0.3s'}}/>
                      </div>
                      <div style={{fontSize:9,color:'#7A8899'}}>{s.done}/{s.total} {s.unit}</div>
                    </div>
                  );
                })}
              </div>

              <button className="home-btn home-btn-blue" onClick={firstIncomplete ? () => onClientView(firstIncomplete.page) : onContinue}>
                {firstIncomplete ? `Resume — ${firstIncomplete.label}` : scored >= total ? 'Review & generate report' : 'Continue assessment'}
              </button>
            </div>
          </>
        )}

        <div className="home-section-label">Actions</div>
        <div className="home-actions-list">
          <div className="home-action-card" onClick={onNewAssessment}>
            <div className="home-action-icon" style={{background:'#E8F0FE',color:'#1565C0',fontSize:20,fontWeight:700}}>+</div>
            <div className="home-action-body">
              <div className="home-action-title">New assessment</div>
              <div className="home-action-desc">Configure client profile, tech stack, and regulatory context. Connect data sources and run a baseline assessment across all 55 controls.</div>
            </div>
            <div className="home-action-arrow">&#8250;</div>
          </div>

          <div className={`home-action-card${hasActive ? '' : ' home-action-disabled'}`} onClick={hasActive ? onRecertify : undefined}>
            <div className="home-action-icon" style={{background: hasActive?'#E8F5E9':'#F5F7F9',color: hasActive?'#2E7D32':'#B0BAC5',fontSize:20}}>&#8635;</div>
            <div className="home-action-body">
              <div className="home-action-title">Recertification run</div>
              <div className="home-action-desc">
                {hasActive
                  ? `Re-assess ${activeEng.org_name}. Copies baseline scores into a new run — update controls that changed. Report shows delta per control.`
                  : 'Complete a baseline assessment first, then return in 3\u201312 months for recertification.'}
              </div>
              {hasActive && latestRun && (
                <div className="home-action-meta">
                  {runCount} run{runCount!==1?'s':''} &nbsp;·&nbsp;
                  Score: <span style={{fontWeight:600,color:scoreColor(os)}}>{os||'\u2014'}</span>
                </div>
              )}
            </div>
            <div className="home-action-arrow" style={{color: hasActive?'#455A64':'#D0D5DA'}}>&#8250;</div>
          </div>
        </div>

        {/* Engagement toolkit */}
        <div className="home-section-label">Engagement toolkit</div>
        <EngagementToolkit sections={['framework','pre_engagement','technical','client_prep','post_engagement']} API={API}/>

        <Footer/>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  //  CLIENT — Executive dashboard (screenshottable for the board)
  // ══════════════════════════════════════════════════════════════
  if (persona === 'client') {
    return (
      <div className="home-container">
        <PersonaBar color="#2E7D32" icon={iconClient} label="Client Stakeholder" displayName={displayName}/>

        {!loading && !hasActive && (
          <EmptyState emoji="&#128274;" title="No active assessment" desc="Contact your Kloudynet assessor to initiate a SAARA assessment for your organisation."/>
        )}

        {!loading && hasActive && (
          <>
            {/* Executive summary card */}
            <div style={{background:'#fff',border:'1px solid #E8ECF0',borderRadius:14,padding:'24px',marginBottom:20}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:20}}>
                <div>
                  <div style={{fontSize:18,fontWeight:700,color:'#0D1B2A',marginBottom:4}}>{activeEng.org_name}</div>
                  <div style={{fontSize:11,color:'#7A8899'}}>
                    AI Security Maturity Assessment &nbsp;·&nbsp; Lead assessor: {activeEng.assessor || 'Kloudynet'} &nbsp;·&nbsp; {scored}/{total} controls scored
                  </div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:36,fontWeight:800,color:os ? scoreColor(os) : '#B0BAC5',lineHeight:1}}>{os || '—'}</div>
                  <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>{os ? `${maturityLabel(os)} / 5.0` : 'Scoring in progress'}</div>
                </div>
              </div>

              {/* Domain maturity bars */}
              <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',marginBottom:8}}>Domain maturity</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 24px'}}>
                {domains.map(d => <DomainBar key={d.id} domain={d}/>)}
              </div>
            </div>

            {/* Top risks — the thing a CISO actually wants to see */}
            {worstDomains.length > 0 && os > 0 && (
              <div style={{marginBottom:20}}>
                <div className="home-section-label">Priority risk areas</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
                  {worstDomains.map((d, i) => (
                    <div key={d.id} style={{
                      padding:'14px 16px',background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,
                      borderLeft:`4px solid ${(d.score||0) < 2 ? '#C62828' : '#F57F17'}`,
                    }}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                        <span style={{fontSize:10,fontWeight:700,color:d.color}}>{d.id}</span>
                        <span style={{fontSize:18,fontWeight:700,color:scoreColor(d.score||0)}}>{d.score||0}</span>
                      </div>
                      <div style={{fontSize:12,fontWeight:600,color:'#0D1B2A',marginBottom:4}}>{d.name}</div>
                      <div style={{fontSize:10,color:'#7A8899'}}>
                        {d.status_counts?.missing||0} gaps &nbsp;·&nbsp; {d.scored}/{d.controls} scored
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick actions */}
            <div className="home-section-label">Your assessment</div>
            <div className="home-actions-list">
              <div className="home-action-card" onClick={() => onClientView('dashboard')}>
                <div className="home-action-icon" style={{background:'#E8F5E9',color:'#2E7D32'}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                </div>
                <div className="home-action-body">
                  <div className="home-action-title">Full assessment overview</div>
                  <div className="home-action-desc">Expand each domain to see individual control scores, statuses, and assessor notes</div>
                </div>
                <div className="home-action-arrow">&#8250;</div>
              </div>
              <div className="home-action-card" onClick={() => onClientView('interviews')}>
                <div className="home-action-icon" style={{background:'#FFF8E1',color:'#F57F17'}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                </div>
                <div className="home-action-body">
                  <div className="home-action-title">Stakeholder interviews</div>
                  <div className="home-action-desc">Respond to structured questions across 6 governance domains. Your responses directly inform control scoring.</div>
                </div>
                <div className="home-action-arrow">&#8250;</div>
              </div>
              <div className="home-action-card" onClick={() => onClientView('report')}>
                <div className="home-action-icon" style={{background:'#E8F0FE',color:'#1565C0'}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </div>
                <div className="home-action-body">
                  <div className="home-action-title">Findings report & PDF export</div>
                  <div className="home-action-desc">Priority findings, 12-month remediation roadmap, licensing recommendations, and attestation</div>
                </div>
                <div className="home-action-arrow">&#8250;</div>
              </div>
            </div>
          </>
        )}
        <Footer/>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  //  PRE-SALES — Deal intelligence dashboard
  // ══════════════════════════════════════════════════════════════
  if (persona === 'presales') {
    // Estimate remediation scope from gap count
    const tier = gaps >= 30 ? 'Enterprise' : gaps >= 15 ? 'Standard' : 'Express';
    const tierWeeks = gaps >= 30 ? '12–16 weeks' : gaps >= 15 ? '6–8 weeks' : '2–3 weeks';
    const skusNeeded = domains.filter(d => (d.score||0) < 3).length;
    const e5Count = SKU_MAP.filter(s => s.sku.includes('E5')).length;

    return (
      <div className="home-container">
        <PersonaBar color="#E65100" icon={iconSales} label="Pre-sales & Sales" displayName={displayName}/>

        {!loading && !hasActive && (
          <EmptyState emoji="&#128202;" title="No active assessment" desc="Once an assessor completes a SAARA engagement, deal intelligence and licensing opportunities will appear here."/>
        )}

        {!loading && hasActive && (
          <>
            {/* Deal qualification card */}
            <div style={{background:'#fff',border:'1px solid #E8ECF0',borderRadius:14,padding:'24px',marginBottom:20}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:16}}>
                <div>
                  <div style={{fontSize:16,fontWeight:700,color:'#0D1B2A',marginBottom:2}}>{activeEng.org_name}</div>
                  <div style={{fontSize:11,color:'#7A8899'}}>{activeEng.industry || 'Industry not specified'} &nbsp;·&nbsp; {activeEng.licensing || 'Licensing not specified'}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <span style={{fontSize:11,fontWeight:600,padding:'4px 12px',borderRadius:8,background: tier==='Enterprise'?'#FFEBEE':tier==='Standard'?'#FFF8E1':'#E8F5E9',color: tier==='Enterprise'?'#C62828':tier==='Standard'?'#F57F17':'#2E7D32'}}>
                    {tier} tier
                  </span>
                </div>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:16}}>
                <MetricCard label="AI maturity" value={os||'—'} sub={os ? maturityLabel(os) : 'Pending'} color={os?scoreColor(os):'#B0BAC5'}/>
                <MetricCard label="Security gaps" value={gaps} sub={`of ${total} controls`} color={gaps>15?'#C62828':'#F57F17'}/>
                <MetricCard label="Remediation scope" value={tierWeeks} sub={`${tier} engagement`}/>
                <MetricCard label="SKU opportunities" value={skusNeeded} sub={`of 6 domains need upgrade`} color="#0078D4"/>
              </div>

              {/* Domain gap severity — sales heat map */}
              <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',marginBottom:8}}>Domain gap severity</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:6,marginBottom:16}}>
                {domains.map(d => {
                  const s = d.score || 0;
                  const gapCount = d.status_counts?.missing || 0;
                  return (
                    <div key={d.id} style={{
                      padding:'10px 8px',borderRadius:8,textAlign:'center',
                      background: s < 2 ? '#FFEBEE' : s < 3 ? '#FFF8E1' : '#E8F5E9',
                      border: `1px solid ${s < 2 ? '#FFCDD2' : s < 3 ? '#FFE082' : '#A5D6A7'}`,
                    }}>
                      <div style={{fontSize:18,fontWeight:700,color:scoreColor(s)}}>{s}</div>
                      <div style={{fontSize:9,fontWeight:600,color:'#455A64',marginTop:2}}>{d.id}</div>
                      <div style={{fontSize:8,color:'#7A8899',marginTop:1}}>{gapCount} gap{gapCount!==1?'s':''}</div>
                    </div>
                  );
                })}
              </div>

              <div style={{display:'flex',gap:8}}>
                <button className="home-btn home-btn-blue" onClick={() => onClientView('dashboard')} style={{flex:1}}>View full gap analysis</button>
                <button className="home-btn home-btn-blue" onClick={() => onClientView('report')} style={{flex:1,background:'#E65100'}}>Export proposal report</button>
              </div>
            </div>

            {/* Licensing upsell table */}
            <div className="home-section-label">Licensing upsell opportunities</div>
            <div style={{background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,overflow:'hidden',marginBottom:20}}>
              <table style={{width:'100%',fontSize:11,borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:'#F8F9FA',borderBottom:'1px solid #E8ECF0'}}>
                    <th style={{padding:'8px 12px',textAlign:'left',fontWeight:600,color:'#455A64',fontSize:10}}>Domain</th>
                    <th style={{padding:'8px 12px',textAlign:'left',fontWeight:600,color:'#455A64',fontSize:10}}>Gap</th>
                    <th style={{padding:'8px 12px',textAlign:'left',fontWeight:600,color:'#455A64',fontSize:10}}>Microsoft product</th>
                    <th style={{padding:'8px 12px',textAlign:'left',fontWeight:600,color:'#455A64',fontSize:10}}>SKU</th>
                    <th style={{padding:'8px 12px',textAlign:'center',fontWeight:600,color:'#455A64',fontSize:10}}>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {SKU_MAP.map((s,i) => {
                    const d = domains.find(x => x.id === s.domain);
                    const ds = d?.score || 0;
                    const needsUpgrade = ds < 3;
                    return (
                      <tr key={i} style={{borderBottom:'1px solid #F0F2F5',opacity:needsUpgrade?1:0.5}}>
                        <td style={{padding:'8px 12px',fontWeight:600,color:d?.color||'#455A64'}}>{s.domain}</td>
                        <td style={{padding:'8px 12px',color:'#455A64'}}>{s.gap}</td>
                        <td style={{padding:'8px 12px',fontWeight:500,color:'#0D1B2A'}}>{s.product}</td>
                        <td style={{padding:'8px 12px'}}><span className="pill" style={{background:'#E3F2FD',color:'#0078D4',fontSize:9}}>{s.sku}</span></td>
                        <td style={{padding:'8px 12px',textAlign:'center',fontWeight:700,color:scoreColor(ds)}}>{ds}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pipeline tracker placeholder */}
            <div className="home-action-card home-action-coming" style={{marginBottom:20}}>
              <div className="home-action-icon" style={{background:'#ECEFF1',color:'#455A64'}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
              </div>
              <div className="home-action-body">
                <div className="home-action-title">Portfolio pipeline tracker</div>
                <div className="home-action-desc">Aggregate view across all client engagements — gap severity, licensing opportunity, proposal status. Requires multi-tenant.</div>
              </div>
              <span className="pill pill-gray" style={{flexShrink:0}}>Coming soon</span>
            </div>
          </>
        )}

        {/* Engagement toolkit — sales gets pre-engagement, client prep, sales collateral, post-engagement */}
        <div className="home-section-label">Engagement toolkit</div>
        <EngagementToolkit sections={['framework','sales','pre_engagement','client_prep','post_engagement']} API={API}/>

        <Footer/>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  //  CSP — Licensing co-sell brief
  // ══════════════════════════════════════════════════════════════
  if (persona === 'csp') {
    const currentLic = activeEng?.licensing || '—';
    const domainsBelow3 = domains.filter(d => (d.score||0) < 3);
    const relevantSKUs = SKU_MAP.filter(s => domainsBelow3.some(d => d.id === s.domain));
    const uniqueSKUs = [...new Set(relevantSKUs.map(s => s.sku))];

    return (
      <div className="home-container">
        <PersonaBar color="#0078D4" icon={iconCSP} label="Microsoft CSP" displayName={displayName}/>

        {!loading && !hasActive && (
          <EmptyState emoji="&#9741;" title="No active assessment" desc="Once a Kloudynet assessor completes a SAARA engagement, licensing recommendations and co-sell opportunities will appear here."/>
        )}

        {!loading && hasActive && (
          <>
            {/* Co-sell brief */}
            <div style={{background:'#fff',border:'1px solid #E8ECF0',borderRadius:14,padding:'24px',marginBottom:20}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:16}}>
                <div>
                  <div style={{fontSize:16,fontWeight:700,color:'#0D1B2A',marginBottom:2}}>{activeEng.org_name}</div>
                  <div style={{fontSize:11,color:'#7A8899'}}>{activeEng.industry || '—'} &nbsp;·&nbsp; {activeEng.country || '—'}</div>
                </div>
                <div style={{textAlign:'right',padding:'6px 14px',background:'#E3F2FD',borderRadius:8,border:'1px solid #90CAF9'}}>
                  <div style={{fontSize:9,color:'#0078D4',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em'}}>Co-sell opportunity</div>
                  <div style={{fontSize:14,fontWeight:700,color:'#0078D4'}}>{uniqueSKUs.length} SKU{uniqueSKUs.length!==1?'s':''}</div>
                </div>
              </div>

              {/* Licensing comparison */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:20}}>
                <div style={{padding:'14px',background:'#F8F9FA',borderRadius:10,borderLeft:'4px solid #B0BAC5'}}>
                  <div style={{fontSize:9,color:'#7A8899',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>Current licensing</div>
                  <div style={{fontSize:14,fontWeight:600,color:'#455A64'}}>{currentLic}</div>
                </div>
                <div style={{padding:'14px',background:'#E3F2FD',borderRadius:10,borderLeft:'4px solid #0078D4'}}>
                  <div style={{fontSize:9,color:'#0078D4',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>Recommended</div>
                  <div style={{fontSize:14,fontWeight:600,color:'#0078D4'}}>{uniqueSKUs.length > 0 ? uniqueSKUs.join(' + ') : 'No upgrade needed'}</div>
                </div>
                <div style={{padding:'14px',background:'#FFF8E1',borderRadius:10,borderLeft:'4px solid #F57F17'}}>
                  <div style={{fontSize:9,color:'#F57F17',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>Domains below target</div>
                  <div style={{fontSize:14,fontWeight:600,color:'#F57F17'}}>{domainsBelow3.length} of 6</div>
                </div>
              </div>

              {/* Per-domain product mapping — driven by actual gap data */}
              <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',marginBottom:10}}>Gap-to-product mapping</div>
              {SKU_MAP.map((s,i) => {
                const d = domains.find(x => x.id === s.domain);
                const ds = d?.score || 0;
                const needsUpgrade = ds < 3;
                if (!needsUpgrade) return null;
                return (
                  <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',marginBottom:6,background:'#FAFBFC',borderRadius:8,border:'1px solid #EEF0F3'}}>
                    <div style={{width:32,height:32,borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,background:ds<2?'#FFEBEE':'#FFF8E1',color:scoreColor(ds)}}>
                      {ds}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:2}}>
                        <span style={{fontSize:10,fontWeight:700,color:d?.color||'#455A64'}}>{s.domain}</span>
                        <span style={{fontSize:11,fontWeight:600,color:'#0D1B2A'}}>{d?.name}</span>
                      </div>
                      <div style={{fontSize:10,color:'#7A8899'}}>{s.gap}</div>
                    </div>
                    <div style={{textAlign:'right',flexShrink:0}}>
                      <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A'}}>{s.product}</div>
                      <span className="pill" style={{background:'#E3F2FD',color:'#0078D4',fontSize:9}}>{s.sku}</span>
                    </div>
                  </div>
                );
              })}
              {domainsBelow3.length === 0 && (
                <div style={{padding:'16px',textAlign:'center',color:'#2E7D32',fontSize:12,fontWeight:500}}>
                  All domains at target maturity — no licensing upgrade required
                </div>
              )}

              <div style={{marginTop:16,display:'flex',gap:8}}>
                <button className="home-btn home-btn-blue" onClick={() => onClientView('dashboard')} style={{flex:1,background:'#0078D4'}}>View SKU pull-through detail</button>
                <button className="home-btn home-btn-blue" onClick={() => onClientView('report')} style={{flex:1,background:'#455A64'}}>Export report with licensing roadmap</button>
              </div>
            </div>
          </>
        )}
        <Footer/>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  //  ADMIN — Platform administration (all coming soon)
  // ══════════════════════════════════════════════════════════════
  if (persona === 'admin') {
    return (
      <div className="home-container">
        <PersonaBar color="#455A64" icon={iconAdmin} label="Kloudynet Admin" displayName={displayName}/>

        <div className="home-section-label">Platform administration</div>
        <div className="home-actions-list">
          {[
            { title:'User management', desc:'Create and manage assessor accounts, assign roles (Assessor, Client, Admin), reset credentials', icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
            { title:'Engagement settings', desc:'Configure default scoring templates, assessment tiers, recertification intervals, and regional office settings', icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg> },
            { title:'Control framework', desc:'View and customise the 55 SAARA controls, manage domain weightings, update framework mappings (NIST AI RMF, ISO 42001, OWASP, ATLAS)', icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
            { title:'Data source connectors', desc:'Configure API connections to Microsoft Graph, Sentinel, Defender for Cloud, Purview, Google Cloud, and AWS', icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg> },
            { title:'Audit log', desc:'View platform activity — engagement creation, score changes, report exports, user logins, and admin actions', icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg> },
          ].map((item, i) => (
            <div key={i} className="home-action-card home-action-coming">
              <div className="home-action-icon" style={{background:'#ECEFF1',color:'#455A64'}}>{item.icon}</div>
              <div className="home-action-body">
                <div className="home-action-title">{item.title}</div>
                <div className="home-action-desc">{item.desc}</div>
              </div>
              <span className="pill pill-gray" style={{flexShrink:0}}>Coming soon</span>
            </div>
          ))}
        </div>

        <div className="info-box info-box-blue" style={{marginBottom:32}}>
          <strong>Phase 4 roadmap:</strong> Admin features require Azure PostgreSQL and multi-tenant authentication.
          Currently in design — user management, RBAC, and connector configuration are planned for the SaaS release.
        </div>
        <Footer/>
      </div>
    );
  }

  return null;
}
